import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, Calendar, Award, Gift, ExternalLink, ShieldAlert, ShieldCheck, ShieldX, ChevronDown, RotateCcw, TrendingUp, ShoppingBag, Clock, Target, AlertTriangle } from 'lucide-react';
import { getMemberDetail, getMemberHealth, issueViolation, overturnViolation, getViolationTypes, MemberDetail, AccountHealth, ViolationType, CustomerMetrics } from '../services/api';

const HEALTH_CONFIG: Record<string, { label: string; barColor: string; textColor: string; bgColor: string }> = {
  Excellent:  { label: 'Excellent',  barColor: 'bg-green-500',  textColor: 'text-green-700',  bgColor: 'bg-green-50'  },
  Good:       { label: 'Good',       barColor: 'bg-blue-500',   textColor: 'text-blue-700',   bgColor: 'bg-blue-50'   },
  Fair:       { label: 'Fair',       barColor: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50' },
  Poor:       { label: 'Poor',       barColor: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50' },
  Critical:   { label: 'Critical',   barColor: 'bg-red-500',    textColor: 'text-red-700',    bgColor: 'bg-red-50'    },
  Suspended:  { label: 'Suspended',  barColor: 'bg-red-700',    textColor: 'text-red-800',    bgColor: 'bg-red-100'   },
  Banned:     { label: 'Banned',     barColor: 'bg-gray-800',   textColor: 'text-gray-900',   bgColor: 'bg-gray-100'  },
};

const CONSEQUENCE_COLORS: Record<string, string> = {
  Warning:    'bg-yellow-100 text-yellow-800',
  Suspension: 'bg-orange-100 text-orange-800',
  Ban:        'bg-red-100 text-red-800',
};

const MemberDetailPage: React.FC = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberDetail | null>(null);
  const [redemptions, setRedemptions] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<CustomerMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Account Health state
  const [health, setHealth] = useState<AccountHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [violationTypes, setViolationTypes] = useState<ViolationType[]>([]);
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [violationNotes, setViolationNotes] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [overturning, setOverturning] = useState<string | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);

  useEffect(() => {
    if (memberId) {
      loadMember(memberId);
      loadHealth(memberId);
      loadViolationTypes();
    }
  }, [memberId]);

  const loadMember = async (id: string) => {
    setIsLoading(true);
    try {
      const result = await getMemberDetail(id);
      setMember(result.member);
      setRedemptions(result.redemptions);
      setMetrics(result.metrics);
    } catch (err: any) {
      setError(err.message || 'Failed to load member');
    } finally {
      setIsLoading(false);
    }
  };

  const loadHealth = async (id: string) => {
    setHealthLoading(true);
    try {
      const h = await getMemberHealth(id);
      setHealth(h);
    } catch {
      // non-fatal
    } finally {
      setHealthLoading(false);
    }
  };

  const loadViolationTypes = async () => {
    try {
      const res = await getViolationTypes();
      setViolationTypes(res.violationTypes);
    } catch { /* non-fatal */ }
  };

  const handleIssueViolation = async () => {
    if (!memberId || !selectedTypeId) return;
    setIssuing(true);
    setHealthError(null);
    try {
      const res = await issueViolation(memberId, selectedTypeId, violationNotes || undefined);
      setHealth(res.health);
      setShowIssueForm(false);
      setSelectedTypeId('');
      setViolationNotes('');
    } catch (e: any) {
      setHealthError(e.message);
    } finally {
      setIssuing(false);
    }
  };

  const handleOverturn = async (violationId: string) => {
    if (!memberId || !window.confirm('Are you sure you want to overturn this violation? This will restore the member\'s score.')) return;
    setOverturning(violationId);
    try {
      const res = await overturnViolation(memberId, violationId);
      setHealth(res.health);
    } catch (e: any) {
      setHealthError(e.message);
    } finally {
      setOverturning(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatPhone = (phone: string) => {
    const digits = phone.replace(/\D/g, '').slice(-10);
    if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    return phone;
  };

  const getTierBadge = (tier: string) => {
    const styles = { Gold: 'bg-yellow-100 text-yellow-800', Silver: 'bg-gray-100 text-gray-800', Bronze: 'bg-orange-100 text-orange-800' };
    return styles[tier as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = { Confirmed: 'bg-blue-100 text-blue-800', Fulfilled: 'bg-green-100 text-green-800', Pending: 'bg-yellow-100 text-yellow-800', Cancelled: 'bg-red-100 text-red-800' };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getSegmentBadge = (segment: string | null) => {
    const styles: Record<string, string> = {
      Champion: 'bg-yellow-100 text-yellow-800',
      Loyal: 'bg-green-100 text-green-800',
      Casual: 'bg-blue-100 text-blue-800',
      New: 'bg-purple-100 text-purple-800',
      AtRisk: 'bg-orange-100 text-orange-800',
      Dormant: 'bg-gray-100 text-gray-600'
    };
    return styles[segment || ''] || 'bg-gray-100 text-gray-800';
  };

  const getChurnRiskColor = (risk: number | null) => {
    if (risk === null) return 'text-gray-400';
    if (risk <= 0.25) return 'text-green-600';
    if (risk <= 0.5) return 'text-yellow-600';
    if (risk <= 0.75) return 'text-orange-600';
    return 'text-red-600';
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return '-';
    return `$${amount.toFixed(2)}`;
  };

  const formatDayOfWeek = (day: number | null) => {
    if (day === null) return '-';
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[day] || '-';
  };

  const formatHour = (hour: number | null) => {
    if (hour === null) return '-';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h = hour % 12 || 12;
    return `${h}${ampm}`;
  };

  const formatChannel = (channel: string | null) => {
    if (!channel) return '-';
    const names: Record<string, string> = {
      shopify: 'Shopify',
      auction_method: 'Auction Method',
      in_store: 'In Store'
    };
    return names[channel] || channel;
  };

  const getViolationStatusBadge = (status: string) => {
    const styles: Record<string, string> = { Active: 'bg-red-100 text-red-800', Expired: 'bg-gray-100 text-gray-500', Overturned: 'bg-green-100 text-green-700' };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) return <div className="p-6"><div className="text-center py-12 text-buggy-gray">Loading...</div></div>;

  if (error || !member) {
    return (
      <div className="p-6">
        <button onClick={() => navigate('/members')} className="flex items-center gap-2 text-buggy-gray hover:text-buggy-dark mb-6">
          <ArrowLeft className="w-5 h-5" /> Back to Members
        </button>
        <div className="text-center py-12 text-red-500">{error || 'Member not found'}</div>
      </div>
    );
  }

  const hc = health ? (HEALTH_CONFIG[health.status] || HEALTH_CONFIG['Good']) : null;
  const selectedVT = violationTypes.find(vt => vt.violationTypeId === selectedTypeId);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button onClick={() => navigate('/members')} className="flex items-center gap-2 text-buggy-gray hover:text-buggy-dark mb-6">
        <ArrowLeft className="w-5 h-5" /> Back to Members
      </button>

      {/* Member Header */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-buggy-dark">{member.firstName} {member.lastName}</h1>
            <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${getTierBadge(member.currentTier)}`}>
              {member.currentTier} Member
            </span>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-buggy-blue">{member.currentPoints}</p>
            <p className="text-buggy-gray text-sm">Current Points</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
          <div className="flex items-center gap-3"><Phone className="w-5 h-5 text-buggy-gray" /><span className="text-buggy-dark">{formatPhone(member.phoneNumber)}</span></div>
          {member.email && <div className="flex items-center gap-3"><Mail className="w-5 h-5 text-buggy-gray" /><span className="text-buggy-dark">{member.email}</span></div>}
          <div className="flex items-center gap-3"><Calendar className="w-5 h-5 text-buggy-gray" /><span className="text-buggy-dark">Enrolled {formatDate(member.enrolledDate)}</span></div>
          <div className="flex items-center gap-3"><Award className="w-5 h-5 text-buggy-gray" /><span className="text-buggy-dark">{member.lifetimePoints} lifetime points</span></div>
        </div>

        <div className="grid grid-cols-3 gap-4 pt-4 mt-4 border-t text-sm">
          <div><p className="text-buggy-gray">Carrier</p><p className="font-medium text-buggy-dark">{member.phoneCarrier || '-'}</p></div>
          <div><p className="text-buggy-gray">Phone Type</p><p className="font-medium text-buggy-dark">{member.phoneType || '-'}</p></div>
          <div><p className="text-buggy-gray">Last Activity</p><p className="font-medium text-buggy-dark">{formatDate(member.lastActivityDate)}</p></div>
        </div>

        {/* Linked Accounts */}
        <div className="pt-4 mt-4 border-t">
          <p className="text-xs font-semibold text-buggy-gray uppercase tracking-wide mb-3">Linked Accounts</p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <span className="text-sm text-buggy-gray">Shopify:</span>
              {member.shopifyCustomerId ? (() => {
                const numericId = member.shopifyCustomerId.split('/').pop() || member.shopifyCustomerId;
                return (
                  <a href={`https://181pxk-e2.myshopify.com/admin/customers/${numericId}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-buggy-blue hover:underline flex items-center gap-1">
                    {numericId}<ExternalLink className="w-3 h-3" />
                  </a>
                );
              })() : <span className="text-sm text-buggy-gray italic">Not linked</span>}
            </div>
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <span className="text-sm text-buggy-gray">AuctionMethod:</span>
              {member.auctionMethodUserId ? (
                <a href={`https://auction.buggybusters.com/myaccount/personalinfo/id/${member.auctionMethodUserId}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-buggy-blue hover:underline flex items-center gap-1">
                  {member.auctionMethodUserId}<ExternalLink className="w-3 h-3" />
                </a>
              ) : <span className="text-sm text-buggy-gray italic">Not linked</span>}
            </div>
          </div>
        </div>
      </div>

      {/* ── Customer Insights ── */}
      {metrics && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-bold text-buggy-dark flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-buggy-blue" />
              Customer Insights
            </h2>
            <div className="flex items-center gap-2">
              {metrics.customerSegment && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSegmentBadge(metrics.customerSegment)}`}>
                  {metrics.customerSegment}
                </span>
              )}
              {metrics.churnRiskScore !== null && metrics.churnRiskScore >= 0.5 && (
                <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800`}>
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Churn Risk: {Math.round(metrics.churnRiskScore * 100)}%
                </span>
              )}
            </div>
          </div>

          <div className="p-6">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-buggy-gray uppercase tracking-wide mb-1">Total Spend</p>
                <p className="text-2xl font-bold text-buggy-dark">{formatCurrency(metrics.totalSpend)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-buggy-gray uppercase tracking-wide mb-1">Transactions</p>
                <p className="text-2xl font-bold text-buggy-dark">{metrics.totalTransactions}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-buggy-gray uppercase tracking-wide mb-1">Avg. Order</p>
                <p className="text-2xl font-bold text-buggy-dark">{formatCurrency(metrics.avgTransactionValue)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-buggy-gray uppercase tracking-wide mb-1">Days Since Visit</p>
                <p className={`text-2xl font-bold ${metrics.currentGapDays && metrics.currentGapDays > 30 ? 'text-orange-600' : 'text-buggy-dark'}`}>
                  {metrics.currentGapDays ?? '-'}
                </p>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Channel Breakdown */}
              <div>
                <h3 className="text-sm font-semibold text-buggy-dark flex items-center gap-2 mb-3">
                  <ShoppingBag className="w-4 h-4 text-buggy-gray" /> Channel Breakdown
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-buggy-gray">Shopify</span>
                    <span className="font-medium">{metrics.shopifyTransactions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-buggy-gray">Auction Method</span>
                    <span className="font-medium">{metrics.auctionMethodTransactions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-buggy-gray">In Store</span>
                    <span className="font-medium">{metrics.inStoreTransactions}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-buggy-gray">Primary Channel</span>
                    <span className="font-medium">{formatChannel(metrics.primaryChannel)}</span>
                  </div>
                  {metrics.isMultiChannel && (
                    <div className="text-xs text-green-600 font-medium">✓ Multi-channel customer</div>
                  )}
                </div>
              </div>

              {/* Timing Preferences */}
              <div>
                <h3 className="text-sm font-semibold text-buggy-dark flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-buggy-gray" /> Shopping Patterns
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-buggy-gray">Preferred Day</span>
                    <span className="font-medium">{formatDayOfWeek(metrics.preferredDayOfWeek)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-buggy-gray">Preferred Time</span>
                    <span className="font-medium">{formatHour(metrics.preferredHour)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-buggy-gray">Weekend %</span>
                    <span className="font-medium">{metrics.weekendTransactionPct !== null ? `${Math.round(metrics.weekendTransactionPct)}%` : '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-buggy-gray">Avg Days Between</span>
                    <span className="font-medium">{metrics.avgDaysBetweenVisits !== null ? `${Math.round(metrics.avgDaysBetweenVisits)} days` : '-'}</span>
                  </div>
                </div>
              </div>

              {/* RFM Scores */}
              <div>
                <h3 className="text-sm font-semibold text-buggy-dark flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4 text-buggy-gray" /> RFM Score
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-buggy-gray">Recency</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className={`w-3 h-3 rounded-full ${i <= (metrics.rfmRecency || 0) ? 'bg-buggy-blue' : 'bg-gray-200'}`} />
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-buggy-gray">Frequency</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className={`w-3 h-3 rounded-full ${i <= (metrics.rfmFrequency || 0) ? 'bg-buggy-blue' : 'bg-gray-200'}`} />
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-buggy-gray">Monetary</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className={`w-3 h-3 rounded-full ${i <= (metrics.rfmMonetary || 0) ? 'bg-buggy-blue' : 'bg-gray-200'}`} />
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-buggy-gray">Churn Risk</span>
                    <span className={`font-bold ${getChurnRiskColor(metrics.churnRiskScore)}`}>
                      {metrics.churnRiskScore !== null ? `${Math.round(metrics.churnRiskScore * 100)}%` : '-'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Date Range */}
            <div className="mt-6 pt-4 border-t text-xs text-buggy-gray flex gap-6">
              <span>First purchase: {formatDate(metrics.firstTransactionDate)}</span>
              <span>Last purchase: {formatDate(metrics.lastTransactionDate)}</span>
              <span>Redemptions: {metrics.redemptionCount}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Account Health ── */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold text-buggy-dark flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-red-500" />
            Account Health
          </h2>
          {!showIssueForm && (
            <button onClick={() => setShowIssueForm(true)} className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
              <ShieldX className="w-4 h-4" /> Issue Violation
            </button>
          )}
        </div>

        {/* Issue Violation Form — rendered outside health conditional so it always shows */}
        {showIssueForm && (
          <div className="px-6 pt-5">
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 space-y-4">
              <h3 className="font-bold text-red-800">Issue a Violation</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Violation Type</label>
                <div className="relative">
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm appearance-none pr-8 focus:outline-none focus:ring-2 focus:ring-red-400"
                    value={selectedTypeId}
                    onChange={e => setSelectedTypeId(e.target.value)}
                  >
                    <option value="">— Select a violation type —</option>
                    {violationTypes.map(vt => (
                      <option key={vt.violationTypeId} value={vt.violationTypeId}>
                        {vt.name} ({vt.consequenceType}{vt.suspensionDays ? `, ${vt.suspensionDays}d` : ''})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                {selectedVT && (
                  <div className="mt-2 p-3 bg-white rounded-lg border text-sm text-gray-600">
                    <p className="mb-1">{selectedVT.description}</p>
                    <p className="text-xs text-gray-500">Score impact: <span className="font-semibold text-red-600">{selectedVT.healthScoreImpact}</span> · Expires after {selectedVT.expiresAfterDays} days</p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff Notes (optional)</label>
                <textarea className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" rows={2} placeholder="e.g. Left trash bags in back row, warned first, refused to clean up" value={violationNotes} onChange={e => setViolationNotes(e.target.value)} />
              </div>
              {healthError && <p className="text-sm text-red-600">{healthError}</p>}
              <div className="flex gap-3">
                <button onClick={handleIssueViolation} disabled={!selectedTypeId || issuing} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                  {issuing ? 'Issuing...' : 'Confirm & Issue'}
                </button>
                <button onClick={() => { setShowIssueForm(false); setSelectedTypeId(''); setViolationNotes(''); }} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {healthLoading ? (
          <div className="p-6 text-center text-gray-400">Loading health data...</div>
        ) : health && hc ? (
          <div className="p-6 space-y-5">

            {/* Health Score Gauge */}
            <div className={`${hc.bgColor} rounded-xl p-5`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className={`w-5 h-5 ${hc.textColor}`} />
                  <span className={`font-bold text-lg ${hc.textColor}`}>{hc.label}</span>
                  {health.isSuspended && health.suspendedUntil && (
                    <span className="text-xs text-red-700 font-medium">until {formatDate(health.suspendedUntil)}</span>
                  )}
                </div>
                <span className={`text-2xl font-bold ${hc.textColor}`}>{health.score}/100</span>
              </div>
              <div className="w-full bg-white rounded-full h-3 shadow-inner">
                <div className={`${hc.barColor} h-3 rounded-full transition-all duration-500`} style={{ width: `${health.score}%` }} />
              </div>
              {(health.isBanned || health.isSuspended) && (
                <p className="text-sm mt-3 font-semibold text-red-800">
                  {health.isBanned ? '⛔ Account permanently banned — member cannot transact or redeem.' : '⏸ Account suspended — member cannot transact or redeem until suspension expires.'}
                </p>
              )}
            </div>

            {/* Violations List */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Violation History</h3>
              {health.violations.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No violations on record</p>
              ) : (
                <div className="space-y-3">
                  {health.violations.map(v => (
                    <div key={v.violationId} className={`border rounded-xl p-4 ${v.status === 'Active' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50 opacity-70'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-semibold text-buggy-dark">{v.violationName}</span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CONSEQUENCE_COLORS[v.consequenceType] || 'bg-gray-100 text-gray-700'}`}>{v.consequenceType}</span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getViolationStatusBadge(v.status)}`}>{v.status}</span>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">{v.violationDescription}</p>
                          {v.notes && <p className="text-sm text-gray-700 italic bg-white rounded px-2 py-1 border border-gray-200 mb-1">"{v.notes}"</p>}
                          <div className="flex gap-4 text-xs text-gray-500">
                            <span>Issued {formatDate(v.issuedDate)}</span>
                            <span>Expires {formatDate(v.expiresDate)}</span>
                            {v.issuedByName && <span>By {v.issuedByName}</span>}
                            <span className="text-red-600 font-medium">{v.healthScoreImpact} pts</span>
                          </div>
                          {v.status === 'Overturned' && v.overturnNotes && (
                            <p className="text-xs text-green-700 mt-1">Overturned: {v.overturnNotes}</p>
                          )}
                        </div>
                        {v.status === 'Active' && (
                          <button
                            onClick={() => handleOverturn(v.violationId)}
                            disabled={overturning === v.violationId}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition flex-shrink-0"
                            title="Overturn this violation"
                          >
                            <RotateCcw size={12} />
                            {overturning === v.violationId ? 'Overturning...' : 'Overturn'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-400">Could not load health data</div>
        )}
      </div>

      {/* Redemptions */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-buggy-dark flex items-center gap-2">
            <Gift className="w-5 h-5" /> Redemptions ({redemptions.length})
          </h2>
        </div>
        {redemptions.length === 0 ? (
          <div className="p-6 text-center text-buggy-gray">No redemptions yet</div>
        ) : (
          <div className="divide-y">
            {redemptions.map(r => (
              <div key={r.redemptionId} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-buggy-dark">{r.rewardName}</p>
                    <p className="text-sm text-buggy-gray">Code: <span className="font-mono">{r.redemptionCode}</span></p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(r.status)}`}>{r.status}</span>
                    <p className="text-sm text-buggy-gray mt-1">{r.pointsRedeemed} pts</p>
                  </div>
                </div>
                <p className="text-xs text-buggy-gray mt-2">
                  Created {formatDate(r.createdDate)}{r.fulfilledDate && ` • Fulfilled ${formatDate(r.fulfilledDate)}`}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberDetailPage;
